import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, ShieldCheck, ArrowLeft, KeyRound } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND) throw new Error('REACT_APP_BACKEND_URL is required');

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // 2FA flow state
  const [twoFaChallenge, setTwoFaChallenge] = useState(null);  // { challenge_token, expires_in_seconds }
  const [twoFaCode, setTwoFaCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const finishLogin = async (token, user) => {
    localStorage.setItem('portal_token', token);
    await refreshUser();
    const from = location.state?.from?.pathname;
    if (['admin', 'manager', 'specialist'].includes(user.role)) {
      navigate(from || '/admin');
    } else {
      navigate(from || '/platform');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND}/api/auth/login`, { email, password });
      if (res.data?.requires_2fa) {
        setTwoFaChallenge({ challenge_token: res.data.challenge_token, expires_in_seconds: res.data.expires_in_seconds });
        toast.info('Voer uw 2FA code in om verder te gaan');
      } else if (res.data?.token && res.data?.user) {
        await finishLogin(res.data.token, res.data.user);
      } else {
        toast.error('Onverwacht antwoord van server');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Inloggen mislukt. Controleer uw gegevens.');
    } finally {
      setLoading(false);
    }
  };

  const handle2faSubmit = async (e) => {
    e.preventDefault();
    if (!twoFaCode || twoFaCode.length < 6) {
      toast.error('Voer uw 6-cijfer code in (of 9-tekens backup code)');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND}/api/auth/2fa/verify`, {
        challenge_token: twoFaChallenge.challenge_token,
        code: twoFaCode.replace(/\s/g, ''),
        is_backup: useBackup,
      });
      if (res.data?.used_backup_code) {
        toast.warning(`Backup-code gebruikt — nog ${res.data.user?.backup_codes_remaining ?? '?'} resterend`);
      }
      await finishLogin(res.data.token, res.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || '2FA verificatie mislukt');
    }
    setLoading(false);
  };

  const cancel2fa = () => {
    setTwoFaChallenge(null);
    setTwoFaCode('');
    setUseBackup(false);
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Left panel — gradient */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)' }}>
        {/* Orb */}
        <div className="absolute top-1/3 left-1/3 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, #D4A017, transparent)' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4A017] rounded-xl flex items-center justify-center">
              <span className="text-[#0f172a] font-bold text-lg">♛</span>
            </div>
            <span style={{ fontFamily: "'Sora', sans-serif" }} className="text-white font-bold text-xl">IMPERIA AI</span>
          </Link>

          {/* Center content */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              style={{ fontFamily: "'Sora', sans-serif" }}
              className="text-4xl font-bold text-white mb-4 leading-tight">
              Welkom terug bij uw groeiplatform
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="text-white/60 text-lg">
              Log in om uw dashboard te bekijken, analyses te lezen en verbeteringen door te voeren.
            </motion.p>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="grid grid-cols-2 gap-4 mt-8">
              {[['238+', 'Actieve bedrijven'], ['€18M+', 'Gegenereerde omzet'], ['95%', 'Klantretentie'], ['4.8★', 'Gemiddelde score']].map(([v, l]) => (
                <div key={l} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
                  <p className="text-[#D4A017] font-bold text-xl">{v}</p>
                  <p className="text-white/50 text-xs">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <p className="text-white/30 text-xs">© 2026 IMPERIA AI. Alle rechten voorbehouden.</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-9 h-9 bg-[#0f172a] rounded-xl flex items-center justify-center">
                <span className="text-[#D4A017] font-bold">♛</span>
              </div>
              <span style={{ fontFamily: "'Sora', sans-serif" }} className="text-[#0f172a] font-bold text-xl">IMPERIA AI</span>
            </Link>
          </div>

          <h1 style={{ fontFamily: "'Sora', sans-serif" }} className="text-3xl font-bold text-[#0f172a] mb-2">
            {twoFaChallenge ? 'Twee-staps verificatie' : 'Inloggen'}
          </h1>
          {!twoFaChallenge && (
            <p className="text-[#64748b] mb-8">Nog geen account? <Link to="/register" className="text-[#D4A017] font-semibold hover:underline">Start gratis scan →</Link></p>
          )}
          {twoFaChallenge && (
            <p className="text-[#64748b] mb-6 flex items-center gap-2"><ShieldCheck size={14} className="text-[#10b981]" /> Vul uw 6-cijferige authenticator-code in voor <strong>{email}</strong></p>
          )}

          {!twoFaChallenge && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">E-mailadres</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="naam@bedrijf.nl" required data-testid="login-email"
                className="rc-input" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">Wachtwoord</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required data-testid="login-password"
                  className="rc-input pr-12" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#D4A017] transition-colors">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full rc-btn rc-btn-dark text-base py-4 rounded-xl justify-center disabled:opacity-60">
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Inloggen...
                </span>
              ) : (
                <span className="flex items-center gap-2">Inloggen <ArrowRight size={18} /></span>
              )}
            </button>
          </form>
          )}

          {twoFaChallenge && (
          <form onSubmit={handle2faSubmit} className="space-y-5" data-testid="2fa-verify-form">
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">{useBackup ? 'Backup-code' : '6-cijfer code'}</label>
              <input
                type="text"
                value={twoFaCode}
                onChange={e => setTwoFaCode(e.target.value)}
                placeholder={useBackup ? "XXXX-XXXX" : "123456"}
                maxLength={useBackup ? 12 : 8}
                autoFocus
                required
                data-testid="2fa-verify-code"
                className="rc-input"
                style={{ fontFamily: 'monospace', fontSize: useBackup ? 16 : 24, textAlign: 'center', letterSpacing: useBackup ? '0.1em' : '0.3em' }}
              />
            </div>

            <button type="submit" disabled={loading} data-testid="2fa-verify-submit"
              className="w-full rc-btn rc-btn-dark text-base py-4 rounded-xl justify-center disabled:opacity-60">
              {loading ? (
                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Verifiëren...</span>
              ) : (
                <span className="flex items-center gap-2"><ShieldCheck size={18} /> Verifieer & login</span>
              )}
            </button>

            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => setUseBackup(!useBackup)} data-testid="2fa-toggle-backup" className="text-[#D4A017] hover:underline inline-flex items-center gap-1">
                <KeyRound size={12} /> {useBackup ? 'Gebruik authenticator code' : 'Backup-code gebruiken'}
              </button>
              <button type="button" onClick={cancel2fa} className="text-[#94a3b8] hover:text-[#0f172a] inline-flex items-center gap-1">
                <ArrowLeft size={12} /> Annuleer
              </button>
            </div>
          </form>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#e2e8f0]" />
            <span className="text-xs text-[#94a3b8] font-medium">of</span>
            <div className="flex-1 h-px bg-[#e2e8f0]" />
          </div>

          {/* Google login — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            data-testid="google-login-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + '/auth/callback';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border-2 border-[#e2e8f0] bg-white hover:bg-[#f8fafc] hover:border-[#D4A017] transition-all text-[#374151] font-semibold text-sm"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Inloggen met Google
          </button>

          <p className="text-center text-[#94a3b8] text-sm mt-6">
            <Link to="/" className="hover:text-[#D4A017] transition-colors">← Terug naar website</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
